/**
 * `org.ts` — taksonomi organisasi & perangkat (§4).
 *
 * Sumber: `design/SCHEMA-v0.md` §4 · `research/VTEC-TEARDOWN.md` §8i · `CLAUDE.md` K4 · K6 · P10.
 *
 * ### Kenapa nilai-nilai ini hidup di `contract`, bukan di skema DDL
 * Ketiganya dipakai **lebih dari satu lapis**: `device.role` ditulis portal, dibaca `pos-web`
 * (menentukan shell mana yang dimuat) dan `device-agent` (menentukan kemampuan apa yang diumumkan).
 * Nilai yang didefinisikan di DDL saja **akan** diketik ulang di klien, dan sekali diketik ulang ia
 * bisa menyimpang tanpa ada yang tahu — persis penyakit yang paket ini obati.
 *
 * @module
 */
/**
 * Peran perangkat (§4 `device.role`).
 *
 * Diturunkan dari `computertype` VTEC (6 peran) + temuan §8i: **453 perangkat pemesanan : 59 kasir**.
 * ⇒ Peran **pemesanan adalah mayoritas**; jangan rancang seolah kasir pusatnya. Konsekuensi
 * komersialnya sudah diambil di `PRD.md` §5: harga per-terminal ditolak karena akan menghukum
 * justru pemakaian ini.
 *
 * | Peran | Butuh akses perangkat? (K4) |
 * |---|---|
 * | `cashier` · `pos_terminal` · `kiosk` | ✅ printer, laci kas, EDC ⇒ perlu `device-agent` |
 * | `waiter_device` · `kds_station` · `kds_summary` · `queue_display` | ❌ web murni |
 * | `print_agent` | ✅ hanya mencetak (K6: printer = data, `host:9100`) |
 *
 * ### ⭐⭐ `cashier` vs `pos_terminal` — HARDWARE-nya boleh sama persis
 * Imron (2026-08-05), dari cara VTEC dipakai grup Arena: *"kalau dia di-set pos_terminal walaupun
 * hardwarenya sama, tetap tidak bisa digunakan untuk tender. tetapi satu outlet boleh memiliki
 * lebih dari 1 device cashier."*
 *
 * ⇒ `cashier` = **till** — boleh menerima uang, punya laci, memegang sesi kas.
 *   `pos_terminal` = **stasiun entri pesanan** — buka bill, kirim ke dapur, cetak tagihan, ⛔ TAK
 *   pernah menyelesaikan pembayaran.
 *
 * ⚠️ Ini sumbu yang **TERPISAH** dari izin staf `payment:take`. Kasir berizin yang berdiri di
 * `pos_terminal` tetap ditolak, dan pelayan tanpa izin tetap ditolak di till. Dua-duanya wajib
 * lolos — satu menjawab "di mesin mana", satunya "oleh siapa".
 */
export const DEVICE_ROLES = Object.freeze([
    'cashier',
    'pos_terminal',
    'kiosk',
    'waiter_device',
    'kds_station',
    'kds_summary',
    'queue_display',
    'print_agent',
]);
const DEVICE_ROLE_SET = new Set(DEVICE_ROLES);
/** Guard {@link DeviceRole}. */
export function isDeviceRole(value) {
    return typeof value === 'string' && DEVICE_ROLE_SET.has(value);
}
/**
 * Peran yang **membutuhkan `device-agent` lokal** (K4).
 *
 * Dihitung dari peran, **tidak disimpan** — dua sumber kebenaran untuk fakta yang sama akan
 * menyimpang (P2). Hanya kasir & kiosk yang menyentuh perangkat keras transaksi; `print_agent`
 * menyentuh printer saja.
 */
export function requiresDeviceAgent(role) {
    return (role === 'cashier' || role === 'pos_terminal' || role === 'kiosk' || role === 'print_agent');
}
/**
 * ⭐⭐ **Perangkat ini boleh menyelesaikan pembayaran?**
 *
 * Dihitung dari peran, **tak disimpan** — kolom `may_tender` yang berdiri sendiri di samping
 * `role` akan suatu hari berselisih dengannya (`kds_station` ber-`may_tender = true` tak masuk
 * akal, tapi tak ada yang menolaknya). Itu persis pelajaran VTEC yang mahal: satu boolean memikul
 * kategori ekonomi (Guardrail #2 — klasifikasi wajib STRUKTURAL).
 *
 * ⛔ `kiosk` sengaja **tidak** termasuk. Kiosk memang menerima pembayaran, tapi lewat jalur yang
 * belum ada di sini (§11 kiosk + EDC terintegrasi, K7) — memasukkannya sekarang berarti
 * menjanjikan kontrol yang belum dijaga apa pun.
 */
export function deviceMayTender(role) {
    return role === 'cashier';
}
/**
 * Jenis usaha sebuah brand (§4 `brand.business_type`).
 *
 * ⚠️ Ada sejak v0 **bukan** karena non-F&B akan dibangun sekarang (`PRD.md` §10 P3 masih terbuka),
 * melainkan karena menambahkannya belakangan berarti membongkar `brand` **dan** `product` yang
 * sudah punya data. Biayanya nol sekarang, mahal nanti.
 */
export const BUSINESS_TYPES = Object.freeze(['fnb', 'retail', 'service']);
const BUSINESS_TYPE_SET = new Set(BUSINESS_TYPES);
/** Guard {@link BusinessType}. */
export function isBusinessType(value) {
    return typeof value === 'string' && BUSINESS_TYPE_SET.has(value);
}
/**
 * Status siklus hidup entitas platform (`tenant`, `subscription`, `device_registry`).
 *
 * ⚠️ **Bukan pengganti soft-delete** (F0-5): `closed` berarti *"berakhir dan tak akan kembali"*,
 * bukan *"baris ini pura-pura tidak ada"*. Baris yang statusnya `closed` tetap terbaca oleh
 * laporan dan tetap punya FK yang sah — dan itu memang yang diinginkan.
 */
export const LIFECYCLE_STATUSES = Object.freeze(['active', 'suspended', 'closed']);
const LIFECYCLE_STATUS_SET = new Set(LIFECYCLE_STATUSES);
/** Guard {@link LifecycleStatus}. */
export function isLifecycleStatus(value) {
    return typeof value === 'string' && LIFECYCLE_STATUS_SET.has(value);
}
/**
 * ⭐⭐⭐ **Tier PENEMPATAN sebuah perangkat** (K13 · dipindah ke DB 2026-08-23).
 *
 * - `server` — ada mini-PC outlet (atau portal terjangkau). Order-to-pay lewat server; layar cuma
 *   menampilkan. **Bawaan**, karena ia perilaku yang sudah berjalan.
 * - `tablet` — TANPA DB lokal & tanpa mini-PC: tablet **menghitung sendiri** dengan
 *   `@osbpos/domain` di atas aturan pajak yang dibawa katalog, lalu mengirim satu dokumen
 *   penjualan sesudah bayar (`POST /sync/sale`).
 *
 * ### ⛔ Kenapa milik PERANGKAT, bukan outlet/tenant
 * Satu outlet bisa punya mini-PC **dan** tablet yang tak tersambung ke mini-PC itu (tablet pelayan
 * keliling, till cadangan). Tier menjawab *"apa yang bisa dijangkau MESIN ini"* — fakta penempatan,
 * bukan jenis usaha (K13).
 *
 * ### ⛔⛔ Kenapa di DATABASE, bukan `localStorage`
 * Sampai 2026-08-23 ia hidup di `localStorage.osbpos.tier` — dan itu **kesalahan yang sama persis
 * dengan `role` sebelum K21**: manajer tak bisa menyetelnya, HQ tak bisa melihat terminal mana yang
 * mode apa, dan siapa pun yang membuka DevTools bisa mengubah **dari mana angka bill datang**.
 * Tier tablet menghitung sendiri dan mengantre penjualan; membalik flag itu mengubah mode UANG
 * sebuah terminal.
 */
export const DEVICE_TIERS = Object.freeze(['server', 'tablet']);
const TIER_SET = new Set(DEVICE_TIERS);
/** Guard {@link DeviceTier}. */
export function isDeviceTier(v) {
    return typeof v === 'string' && TIER_SET.has(v);
}
/**
 * ⭐⭐⭐ **Tier DITURUNKAN, tak pernah disetel orang** (Imron, 2026-08-23).
 *
 * Imron: *"kenapa kamu membuat sesuatu yang mudah jadi complicated… alasannya apa kamu tidak
 * membuat jadi auto?"* — menolak pemilih manual yang sempat dibuat, dan benar: seluruh bahan
 * jawabannya sudah ada di server. `GET /ready` bahkan sudah membedakan mini-PC dari portal, dan
 * layar kasir memanggilnya tiap 5 detik lalu membuang informasinya.
 *
 * ### Aturannya tiga baris, dan tiap baris punya sebab
 * 1. **Dilayani mini-PC ⇒ `server`.** Hitungan terjadi di mesin yang berdiri di outlet itu; tak ada
 *    alasan menghitungnya dua kali.
 * 2. **`quick_service` ⇒ `tablet`.** Inilah warung/kedai yang K13 maksud: tak ada meja untuk
 *    dibagi antar perangkat.
 *    ⭐ **Termasuk yang ber-`kitchen_routing`** sejak 2026-08-23: tiket dapurnya dirakit &
 *    dicetak DI LAYAR (`assembleLocalKitchenTickets`), dan baris `kitchen_ticket`-nya tetap lahir
 *    di server saat dokumen penjualannya menyusul. Sebelum itu, warung berdapur ikut berhenti
 *    jualan tiap internet putus — padahal justru merekalah yang paling sering putus.
 * 3. **`table_service` ⇒ `server`.** Meja adalah keadaan yang DIBAGI antar perangkat: dua tablet
 *    yang menghitung sendiri tak bisa saling melihat meja terbuka, dan yang kedua akan membuka
 *    meja yang sudah dipakai. Itu batas kebenaran, bukan pilihan gaya.
 *
 * ### ⛔ Yang BUKAN pembedanya: jumlah till
 * Sempat dikira begitu, dan salah. Satu-satunya keadaan bersama di quick service — nomor antrean &
 * daftar tertahan — **tetap lewat server bahkan di tier tablet**. Dua till karena itu tak mungkin
 * sama-sama menerbitkan "#4": saat server ada nomornya urut, saat server hilang menahan tak jalan
 * sama sekali alih-alih menyimpang. Aturan "cuma boleh satu till" tak menjaga apa pun.
 *
 * ⚠️ Baris 3 adalah batas KEBENARAN, bukan batas hari ini — berbeda dari `kitchen_routing` yang
 * memang cuma butuh dikerjakan. Meja yang dibagi tak bisa "disinkronkan belakangan": tamu sudah
 * telanjur duduk di meja yang sama.
 */
export function deriveDeviceTier(input) {
    if (input.servedByOutletServer)
        return 'server';
    if (input.saleMode !== 'quick_service')
        return 'server';
    return 'tablet';
}
//# sourceMappingURL=org.js.map